import { MIN_PLAYERS, MAX_PLAYERS, roleDistribution } from './config.js';
/**
 * The games this server can run (matches the store manifest ids + GAME_RULES
 * keys). Exported as a TUPLE, not just a union, so runtime validators can be
 * DERIVED from it (`z.enum(GAME_IDS)`) instead of hand-retyping the list. The
 * zod schemas used to carry their own literal `['bloodmoon','whisper']` enums,
 * which silently rejected any newly-added game until someone remembered to
 * update them in two more places. Add a game HERE and validation follows.
 */
export const GAME_IDS = ['bloodmoon', 'whisper', 'onenight'];
// ─── The Whisper ───────────────────────────────────────────────────────────────
/**
 * Floor raised 2 → 3 (2026-07-28) on production evidence. At 2 players The Whisper
 * is not a deduction game at all: no Deceiver, no Paladin, no HUNT phase (see
 * `huntRuns` in the engine), so the round is `QUESTION → END` and the winner is
 * simply whoever scored higher. Four such games completed in prod and NOT ONE was
 * replayed, while 3p (which does run the hunt) and Blood Moon at 7–8p both got
 * repeat sessions. The distribution table below starts at 3 to match.
 */
export const WHISPER_MIN_PLAYERS = 3;
export const WHISPER_MAX_PLAYERS = 7;
/**
 * The Whisper is a ONE-ROUND game (like Blood Moon: runs to a single win/lose
 * END, no multi-round session). Kept at 1 (not removed) so `GameRules.totalRounds`
 * / `WhisperPublic.totalRounds` stay meaningful "1 of 1" fields for the client.
 */
export const WHISPER_TOTAL_ROUNDS = 1;
/**
 * The Whisper role distribution (THE_WHISPER_SPEC.md §3), keyed by table size.
 * Deceivers cap at 2. Villagers fill the remainder. Starts at 3 — the smallest
 * table that still has a Deceiver to hunt (see WHISPER_MIN_PLAYERS).
 *
 *   n | DECEIVER | PALADIN | VILLAGER
 *   3 |    1     |    0    |    2
 *   4 |    1     |    1    |    2
 *   5 |    2     |    2    |    1
 *   6 |    2     |    2    |    2
 *   7 |    2     |    2    |    3
 */
export function whisperDistribution(n) {
    if (n < WHISPER_MIN_PLAYERS || n > WHISPER_MAX_PLAYERS) {
        throw new Error(`whisperDistribution: playerCount ${n} out of range [${WHISPER_MIN_PLAYERS}, ${WHISPER_MAX_PLAYERS}]`);
    }
    const deceivers = n <= 4 ? 1 : 2;
    const paladins = n <= 3 ? 0 : n <= 4 ? 1 : 2;
    const villagers = n - deceivers - paladins;
    return { DECEIVER: deceivers, PALADIN: paladins, VILLAGER: villagers };
}
export const WHISPER_RULES = {
    id: 'whisper',
    minPlayers: WHISPER_MIN_PLAYERS,
    maxPlayers: WHISPER_MAX_PLAYERS,
    distribution: whisperDistribution,
    totalRounds: WHISPER_TOTAL_ROUNDS,
    theme: "The Seer's tent",
    // Retired 2026-07-29. It was the intended low-count on-ramp and it did not
    // land: 4 completed games, zero replays, while Blood Moon got repeat
    // sessions. Blood Moon: One Night now owns the 3-7 slot, so the product is
    // two modes of ONE game rather than a menu of separate ones. Kept in the
    // codebase (engine + tests still green), just not offered.
    offered: false,
};
// ─── Blood Moon ────────────────────────────────────────────────────────────────
/**
 * Blood Moon rules — a thin wrapper over the existing globals so both games share
 * one descriptor shape. `totalRounds` is 0 (Blood Moon runs until a win, not a
 * fixed round count — the field is Whisper-oriented and unused here).
 */
export const BLOOD_MOON_RULES = {
    id: 'bloodmoon',
    minPlayers: MIN_PLAYERS,
    maxPlayers: MAX_PLAYERS,
    distribution: roleDistribution,
    totalRounds: 0,
    theme: 'Ravenhollow',
    offered: true,
};
// ─── Blood Moon: One Night ─────────────────────────────────────────────────────
export const ONE_NIGHT_MIN_PLAYERS = 3;
export const ONE_NIGHT_MAX_PLAYERS = 7;
/** One night, one vote, one ending — a single-round game like the other two. */
export const ONE_NIGHT_TOTAL_ROUNDS = 1;
/** How many dealt cards are held back face-down as the centre. */
export const ONE_NIGHT_CENTRE_CARDS = 3;
/**
 * Blood Moon: One Night role pool, keyed by table size.
 *
 * Returns `n + ONE_NIGHT_CENTRE_CARDS` cards — MORE than there are seats, on
 * purpose. Three cards never reach a player, so the table can never solve the
 * game by counting heads: both Werewolves may be sitting in the centre, and the
 * Seer you are arguing with may not exist. That uncertainty is what makes the
 * game work at 3–4 players, where Blood Moon proper collapses.
 *
 * The five special cards are always in the POOL (never guaranteed in play);
 * Villagers fill the rest.
 *
 *   n | pool | WEREWOLF | SEER | THIEF | JOKER | VILLAGER
 *   3 |   6  |    2     |  1   |   1   |   1   |    1
 *   4 |   7  |    2     |  1   |   1   |   1   |    2
 *   5 |   8  |    2     |  1   |   1   |   1   |    3
 *   6 |   9  |    2     |  1   |   1   |   1   |    4
 *   7 |  10  |    2     |  1   |   1   |   1   |    5
 */
export function oneNightDistribution(n) {
    if (n < ONE_NIGHT_MIN_PLAYERS || n > ONE_NIGHT_MAX_PLAYERS) {
        throw new Error(`oneNightDistribution: playerCount ${n} out of range [${ONE_NIGHT_MIN_PLAYERS}, ${ONE_NIGHT_MAX_PLAYERS}]`);
    }
    const werewolves = 2;
    const seers = 1;
    const thieves = 1;
    const jokers = 1;
    const villagers = n + ONE_NIGHT_CENTRE_CARDS - werewolves - seers - thieves - jokers;
    return {
        WEREWOLF: werewolves,
        SEER: seers,
        THIEF: thieves,
        JOKER: jokers,
        VILLAGER: villagers,
    };
}
export const ONE_NIGHT_RULES = {
    id: 'onenight',
    minPlayers: ONE_NIGHT_MIN_PLAYERS,
    maxPlayers: ONE_NIGHT_MAX_PLAYERS,
    distribution: oneNightDistribution,
    totalRounds: ONE_NIGHT_TOTAL_ROUNDS,
    theme: 'Ravenhollow, one night',
    offered: true,
};
/** Every shipped game's rules, keyed by id — the server's game registry seed. */
export const GAME_RULES = {
    [BLOOD_MOON_RULES.id]: BLOOD_MOON_RULES,
    [WHISPER_RULES.id]: WHISPER_RULES,
    [ONE_NIGHT_RULES.id]: ONE_NIGHT_RULES,
};
//# sourceMappingURL=games.js.map